import {
    Calendar,
    CheckCircle,
    Clock,
    PlayCircle,
    Star,
    SearchCheck,
  } from "lucide-react";
  import React, { useState, useEffect } from "react";
  import { ExpandableTile } from "../pages/infoTile";
  import dayjs from "dayjs";
  import { useRouter } from "next/router";
  import Image from "next/image";
  
  // Call to Action Button Component
  const CallToActionButton = ({
    className = "",
    handleClick,
    ctaText,
  }: {
    className?: string;
    handleClick: any;
    ctaText?: any;
  }) => (
    <button
      className={`max-sm:hidden bg-gradient-to-r from-yellow-800 via-yellow-500 to-yellow-600 !text-white font-bold text-lg md:text-xl px-6 md:px-8 py-4 md:py-5 rounded-md flex items-center gap-3 !border-none shadow-sm ${className} animate-pulse`}
      onClick={handleClick}
    >
      <span>{ctaText ?? "Unlock World's First AI Based BNN Tool"}</span>
    </button>
  );
  
  // Feature Card Component
  const FeatureCard = ({
    icon,
    title,
    description,
  }: {
    icon: React.ReactNode;
    title: string;
    description: string;
  }) => (
    <div className="border border-white/20 bg-neutral-400/10 p-6 md:p-8 rounded-xl shadow-lg">
      <div className="flex items-start gap-4">
        <div className="flex items-center justify-center !w-16 h-auto md:!w-20 md:h-auto aspect-square rounded-lg">
          {icon}
        </div>
        <div className="flex-1 items-center">
          <h3 className="text-sm md:text-lg font-semibold text-white mb-3">
            {title}
          </h3>
          <div className="text-white text-sm md:text-base leading-relaxed">
            {description}
          </div>
        </div>
      </div>
    </div>
  );
  
  // Bonus Card Component
  const BonusCard = ({
    number,
    title,
    description,
    value,
    icon,
  }: {
    number: string;
    title: string;
    description: string;
    value: string;
    icon: string;
  }) => (
    <div className="relative bg-gradient-to-b from-white to-gray-100 rounded-3xl border-2 border-white p-8 shadow-lg">
      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-black to-yellow-600 text-white px-6 py-3 rounded-full text-lg font-bold">
        BONUS {number}
      </div>
      <div className="flex flex-col items-center text-center mt-8">
        <img src={icon} alt="" className="w-20 h-20 mb-6" />
        <h3 className="text-2xl font-bold text-black mb-4 leading-tight">
          {title}
        </h3>
        <div className="text-black text-lg mb-6 leading-relaxed">
          {description}
        </div>
        <div className="text-xl font-bold text-black">{value}</div>
      </div>
    </div>
  );
  
  // Mentor Card Component
  const MentorCard = ({
    name,
    image,
    achievements,
  }: {
    name: string;
    image: string;
    achievements: string[];
  }) => (
    <div className="grid md:grid-cols-2 gap-8 items-center">
      <img
        src={image}
        alt={name}
        className="w-full h-auto object-cover rounded-2xl border border-yellow-500/50"
      />
      <div className="flex flex-col items-start">
        <h3 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-8 text-center">
          {name}
        </h3>
        <div className="space-y-4 flex flex-col w-82">
          {achievements.map((achievement, index) => (
            <div key={index} className="flex items-start  gap-3">
              <CheckCircle className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
              <div className="text-white text-lg leading-relaxed">
                {achievement}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  // Main Landing Page Component
  export function DineshAiComponent({
    setLeadFormOpen,
    activeSlot,
    ctaText,
    formCtaText,
    bucket
  }: {
    setLeadFormOpen: (args: any) => void;
    activeSlot: any;
    ctaText: string;
    formCtaText: string;
    bucket?: string;
  }) {
    const [openFAQ, setOpenFAQ] = useState<number | null>(null);
    const [isClient, setIsClient] = useState(false);
    const router = useRouter();
    const source = router.query?.source;
  
    useEffect(() => {
      setIsClient(true);
    }, []);
  
    // Features data
    const features = [
      {
        icon: (
          <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center text-[44px]">
            📊
          </div>
        ),
        title:
          "Use Gold Tree Astro to identify key life events & insights in just 2 minutes",
        description: "",
      },
      {
        icon: (
          <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-blue-500 rounded-lg flex items-center justify-center text-[44px]">
            📈
          </div>
        ),
        title:
          "Make faster & more accurate life decisions using astrology predictions",
        description: "",
      },
      {
        icon: (
          <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center text-[44px]">
            🎯
          </div>
        ),
        title:
          "Predict career, relationships, health & wealth without prior astrology knowledge",
        description: "",
      },
      {
        icon: (
          <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center text-[44px]">
            💼
          </div>
        ),
        title:
          "Achieve personal growth and align your life with the top 1% of successful planners",
        description: "",
      },
      {
        icon: (
          <div className="w-full h-full bg-gradient-to-br from-indigo-400 to-purple-500 rounded-lg flex items-center justify-center text-[44px]">
            🔍
          </div>
        ),
        title:
          "Use AI to uncover hidden patterns in your birth chart for smarter life choices",
        description: "",
      },
      {
        icon: (
          <div className="w-full h-full bg-gradient-to-br from-pink-400 to-red-500 rounded-lg flex items-center justify-center text-[44px]">
            💎
          </div>
        ),
        title:
          "Pin-point the best remedies, timings, and actions with deep astrological analysis",
        description: "",
      },
    ];
  
    const learnSection = [
      "Ideate & create powerful life frameworks using Gold Tree Astro in minutes.",
      "Use AI to decode your career, relationships, wealth & health patterns like a pro.",
      "Identify key life events & remedies before others & secure untapped opportunities in your life.",
      "Put your personal growth on fast track with AI. Be smarter, faster, and more aligned. Maximize your life potential",
      "Do professional-grade astrology analysis in just 2 minutes using Gold Tree Astro",
    ];
  
    return (
      <div
        className="min-h-screen bg-black text-white"
        style={{ fontFamily: "Montserrat, sans-serif" }}
      >
        {/* Font import moved to _document.tsx to avoid hydration issues */}
  
        {/* Hero Section */}
        <section className="relative min-h-screen flex flex-col items-center">
        {(source === "zepto" ||
                source === "swiggy" ||
                source == "gpay" ||
                source == "zomato" ||
                source == "phonepe" ||
                source == "paytm") && (
                <span className=" text-sm flex items-center justify-center flex-wrap pt-10">
                  {bucket == "A" ? (
                    <span>
                      Register Now for{" "}
                      <span className="mx-1 relative">
                        <div className="w-[106%] bg-red-500 h-[2px] absolute -rotate-[22deg] -left-[2px] bottom-1/3" />
                        ₹999
                      </span>{" "}
                      FREE for{" "}
                    </span>
                  ) : (
                    <span> In Exclusive Partnership with </span>
                  )}
                  {source === "zepto" ? (
                    <span className="text-fuchsia-500 font-bold italic mx-1">
                      zepto
                    </span>
                  ) : source === "gpay" ? (
                    <span className="mx-1 ">
                      <Image
                        src={"/dinesh_nadi_ai/Gpay.png"}
                        width={50}
                        height={50}
                        alt=""
                        className="w-auto h-[20px] relative bg-white px-1 py-0.5 rounded-sm shadow-sm"
                      />
                    </span>
                  ) : source == "zomato" ? (
                    <span className="text-[#f41f4f] font-bold italic mx-1">
                      zomato
                    </span>
                  ) : source == "phonepe" ? (
                    <span>
                      {" "}
                      <span className="bg-[#5F259F] text-white px-1.5 py-0.5 rounded-full ml-1">
                        पे
                      </span>{" "}
                      PhonePe
                    </span>
                  ) : source == "paytm" ? (
                    <span className="text-white font-bold italic mx-1">
                      paytm
                    </span>
                  ) : (
                    <span className="text-amber-500 font-bold italic mx-1">
                      swiggy
                    </span>
                  )}{" "}
                  {bucket == "A" ? "Customers" : ""}
                </span>
              )}
          <div className="container mx-auto px-4 py-5 max-w-7xl">
            <div className="text-center sm:mb-12">
              <div className="text-lg md:text-2xl mb-4">
                <span className="text-white">LIMITED SPOTS </span>-
                <span className="text-yellow-400 font-bold"> LIVE SESSION </span>-
                <span className="text-white"> 100% Accurate prediction</span>
              </div>
              <div className="text-gray-400 italic my-2 text-xs">
                25000+ learners already enrolled!
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-5xl font-bold leading-tight sm:mb-6">
                <span className="text-white">INDIA's </span>
                <span className="text-yellow-500">
                  1st AI ASTROLOGY
                  <br /> MASTERCLASS{" "}
                </span>
                <span className="text-white">WITH </span>
                <span className="text-yellow-500">DR. DINESH GURUJI</span>
              </h1>
            </div>
  
            <div className="grid lg:grid-cols-2 gap-12 items-center ">
              {/* Video Section */}
              <div className="relative max-sm:grid max-sm:grid-cols-1 max-sm:gap-4 ">
                <img
                  src="/dinesh_nadi_ai/banner.webp"
                  alt="Workshop Preview"
                  className="w-full rounded-md shadow-2xl max-sm:order-2 filter contrast-125"
                />
  
                {/* Workshop Details Overlay */}
                <div className="mt-6 grid grid-cols-2 gap-4 max-sm:order-1">
                  <div className="rounded-lg shadow-sm p-4 flex items-center gap-3 border border-white/30">
                    <Calendar className="text-yellow-600" />
                    <div>
                      <div className="font-bold text-white text-sm">
                        {isClient && activeSlot?.startDateTime
                          ? dayjs(activeSlot.startDateTime).format("DD MMMM YYYY")
                          : "Loading..."}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg shadow-sm p-4 flex items-center gap-3 border border-white/30">
                    <Clock className="text-yellow-600" />
                    <div>
                      <div className="font-bold text-sm">
                        {isClient && activeSlot?.startDateTime
                          ? dayjs(activeSlot.startDateTime).format("HH:mm A")
                          : "Loading..."}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg shadow-sm p-4 flex items-center gap-3 border border-white/30">
                    <Clock className="text-yellow-600" />
                    <div>
                      <div className="font-bold text-sm">2+ hours</div>
                    </div>
                  </div>
                  <div className="rounded-lg shadow-sm p-4 flex items-center gap-3 border border-white/30">
                    <PlayCircle className="text-yellow-600" />
                    <div>
                      <div className="font-bold text-sm">Live Session</div>
                    </div>
                  </div>
                </div>
              </div>
  
              {/* CTA Section */}
              <div className="text-center lg:text-left">
                <h2 className="text-2xl md:text-3xl font-bold mb-8">
                  <span className="text-white">One </span>
                  <span className="text-yellow-500">App,</span>
                  <span className="text-white"> All </span>
                  <span className="text-yellow-500"> Predictions, </span>
                  <span className="text-white"> Real </span>
                  <span className="text-yellow-400">Solution</span>
                </h2>
  
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-1">
                    <CheckCircle className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
                    <div className="text-sm lg:text-lg font-semibold text-white text-left">
                      Gold Tree Astro will do hours of astrology
                      calculations in minutes — no prior astrology knowledge
                      needed!
                    </div>
                  </div>
                  <div className="flex items-start gap-1">
                    <CheckCircle className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
                    <div className="text-sm lg:text-lg font-semibold text-white text-left">
                      Gold Tree Astro will exactly tell you key events,
                      remedies, and predictions for your life.
                    </div>
                  </div>
                  <div className="flex items-start gap-1">
                    <CheckCircle className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
                    <div className="text-sm lg:text-lg font-semibold text-white text-left">
                      Gold Tree Astro will create personalized life
                      guidance as per your birth chart and expectations.
                    </div>
                  </div>
                </div>
  
                <div className="flex flex-col items-center lg:items-start gap-6">
                  <CallToActionButton
                    className="w-full lg:w-auto"
                    handleClick={() => setLeadFormOpen(true)}
                    ctaText={ctaText}
                  />
                  <div className="text-gray-400 italic">
                    *No Prior Experience Needed*
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
  
        {/* AI Tools Carousel Section */}
        <section className="py-20 bg-black">
    <div className="container mx-auto px-4 text-center max-w-7xl">
      <h2 className="text-3xl md:text-4xl font-medium text-white mb-12 leading-tight">
        The World's Finest Nakshatra Nadi Ai
        <br />
        <span className="text-yellow-500">Saarthi..!</span>
      </h2>
  
      {/* Scroll container */}
      <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide md:justify-center md:gap-12 md:overflow-visible">
        <img
          src="/dinesh_nadi_ai/phone1.webp"
          alt="Phone 1"
          className="h-80 md:h-[30rem] object-contain flex-shrink-0 snap-center mx-2 w-[45vw] md:w-auto rounded-md"
        />
        <img
          src="/dinesh_nadi_ai/phone2.webp"
          alt="Phone 2"
          className="h-80 md:h-[30rem] object-contain flex-shrink-0 snap-center mx-2 w-[45vw] md:w-auto rounded-md"
        />
        <img
          src="/dinesh_nadi_ai/phone3.webp"
          alt="Phone 3"
          className="h-80 md:h-[30rem] object-contain flex-shrink-0 snap-center mx-2 md:mx-0 w-[45vw] md:w-auto rounded-md"
        />
      </div>
    </div>
  </section>
  
  
        {/* Why Attend Workshop Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-medium text-white text-center mb-16">
              WHY ATTEND THIS <span className="text-yellow-500">WORKSHOP?</span>
            </h2>
  
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
  
            <div className="text-center mt-16">
              <CallToActionButton
                className="mx-auto"
                handleClick={() => setLeadFormOpen(true)}
                ctaText={ctaText}
              />
              <div className="text-gray-400 italic mt-4">
                *No Prior Experience Needed*
              </div>
            </div>
          </div>
        </section>
  
        {/* Reviews Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 text-center max-w-7xl">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-yellow-400 fill-current" />
              ))}
            </div>
  
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 underline">
              <span className="text-yellow-400">25,000</span> PAST ATTENDEES...
            </h2>
  
            <div className="md:text-lg text-lg text-neutral-400 mb-16">
              Recommend You To Attend This Workshop If You Fit Any Of The
              Following ⬇️ Beginner
            </div>
  
            <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
              <div className="text-center">
                <img
                  src="/dinesh_nadi_ai/icon1.webp"
                  alt="Beginner"
                  className="w-32 h-32 mx-auto mb-6"
                />
                <h3 className="text-xl font-bold mb-4">
                  <span className="text-white">You're A </span>
                  <span className="text-yellow-400">Beginner</span>
                  <span className="text-white">
                    {" "}
                    Wanting To Understand Your Life & Unlock Personal Growth
                  </span>
                </h3>
                <div className="text-gray-300 leading-relaxed">
                  From students to professionals, discover how Nakshatra Nandi
                  Saarthi AI can empower you to understand your destiny, make
                  better decisions, and plan your life without prior astrology
                  knowledge.
                </div>
              </div>
  
              <div className="text-center">
                <img
                  src="/dinesh_nadi_ai/icon2.webp"
                  alt="Trader"
                  className="w-32 h-32 mx-auto mb-6"
                />
                <h3 className="text-xl font-bold mb-4">
                  <span className="text-white">You're </span>
                  <span className="text-yellow-400">Curious</span>
                  <span className="text-white">
                    {" "}
                    About Life & Want To Discover Hidden Patterns
                  </span>
                </h3>
                <div className="text-gray-300 leading-relaxed">
                  Mid-level seekers and curious minds, dive into the world of
                  AI-powered astrology to uncover insights about career,
                  relationships, health, and wealth.
                </div>
              </div>
  
              <div className="text-center">
                <img
                  src="/dinesh_nadi_ai/icon3.webp"
                  alt="Experienced Trader"
                  className="w-32 h-32 mx-auto mb-6"
                />
                <h3 className="text-xl font-bold mb-4">
                  <span className="text-white">You're An </span>
                  <span className="text-yellow-400">Experienced</span>
                  <span className="text-white">
                    {" "}
                    Ready To Master Astrology For Life Guidance
                  </span>
                </h3>
                <div className="text-gray-300 leading-relaxed">
                  Seasoned enthusiasts, harness the full potential of Nakshatra Nandi
                  Saarthi AI to decode advanced life patterns, remedies and
                  predictions for maximum impact.
                </div>
              </div>
            </div>
          </div>
        </section>
  
        {/* AI Benefits Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-5xl font-medium text-white text-center mb-16 leading-tight">
              Here's How Gold Tree Astro Will Change{" "}
              <span className="text-yellow-500">Astrology Forever</span>
            </h2>
  
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="blue-gradient-box p-8 md:p-12 rounded-3xl">
                <div className="border-l-8 border-yellow-400 pl-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                    <span className="text-white">Mantra To </span>
                    <span className="text-yellow-400">Unlock Your Life's</span>
                    <span className="text-white"> Full Potential</span>
                  </h3>
                  <div className="text-sm md:text-lg text-white leading-relaxed">
                    Yield the power of Gold Tree Astro to consistently
                    discover hidden insights in your life — career, relationships,
                    health, and wealth — while others remain unaware. Be Smarter,
                    Faster & More Aligned Than 99% Others.
                  </div>
                </div>
              </div>
  
              <div className="blue-gradient-box p-8 md:p-12 rounded-3xl">
                <div className="border-l-8 border-yellow-400 pl-8">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6">
                    <span className="text-white">
                      {" "}
                      Witness AI-Powered Astrology in{" "}
                      <span className="text-yellow-400">Live</span> Action!
                    </span>
                  </h3>
                  <div className="text-sm md:text-lg text-white leading-relaxed">
                    Join our LIVE session to analyze your birth chart, uncover
                    hidden patterns, and get personalized guidance with AI-driven
                    astrology predictions.
                  </div>
                </div>
              </div>
  
              <div className="blue-gradient-box p-8 md:p-12 rounded-3xl">
                <div className="border-l-8 border-yellow-400 pl-8">
                  <h3 className="text-2xl md:text-3xl font-bold mb-6">
                    <span className="text-yellow-400">2 Minute</span>
                    <span className="text-white"> AI-Powered Analysis</span>
                  </h3>
                  <div className="text-sm md:text-lg text-white leading-relaxed">
                    Unlock exclusive, groundbreaking AI-powered tools. From
                    predicting key life events, identifying remedies, to decoding
                    your future — months of professional-grade astrological
                    analysis in just 2 minutes!
                  </div>
                </div>
              </div>
            </div>
  
            <div className="text-center mt-16">
              <CallToActionButton
                className="mx-auto"
                handleClick={() => setLeadFormOpen(true)}
                ctaText={ctaText}
              />
              <div className="text-gray-400 italic mt-4">
                *No Prior Experience Needed*
              </div>
            </div>
          </div>
        </section>
  
        {/* What You'll Learn Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              THIS WORKSHOP WILL{" "}
              <span className="text-yellow-500">EMPOWER YOU</span> TO 👇
            </h2>
  
            <div className="flex flex-col mx-auto gap-2 items-center justify-center">
              <div className="mx-auto flex flex-col gap-4">
                {learnSection.map((l) => (
                  <div
                    className="flex items-center gap-1 text-lg text-balance text-white leading-tight bg-white/10 border border-white/20 px-3 py-2 rounded-md"
                    key={l}
                  >
                    <SearchCheck className="text-yellow-500 md:h-8 md:w-8 h-16 w-16" />
                    <span className="text-sm md:text-lg">{l}</span>
                  </div>
                ))}
              </div>
            </div>
  
            <div className="text-center space-y-8 mt-20">
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                THE DECISION IS YOURS TO MAKE...
              </h2>
  
              <div className="text-xl font-bold leading-relaxed">
                <span className="text-white">Ready To Craft Your Own </span>
                <span className="text-yellow-500">
                  100% AI-Driven Life Guidance?
                </span>
              </div>
  
              <CallToActionButton
                className="mx-auto"
                handleClick={() => setLeadFormOpen(true)}
                ctaText={ctaText}
              />
  
              <div className="text-xl font-bold leading-relaxed">
                <span className="text-white">
                  With AI, success in life planning, predictions & remedies is not
                  just a{" "}
                </span>
                <span className="text-yellow-400">Dream</span>
                <span className="text-white">, but </span>
                <span className="text-yellow-400">YOUR</span>
                <span className="text-white">
                  {" "}
                  Gateway to making it a reality!
                </span>
              </div>
  
              <div className="text-gray-400 italic">
                *No Prior Experience Needed*
              </div>
            </div>
          </div>
        </section>
  
        {/* Mentors Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">
              <span className="text-white">MEET YOUR </span>
              <span className="text-yellow-400">MENTOR</span>
            </h2>
  
            <div className="space-y-20">
              <MentorCard
                name="DR. DINESH GURUJI"
                image="/dinesh_nadi_ai/teacher.webp"
                achievements={[
                  "30+ years of experience in Astrology",
                  "Taught more than 100,000 students",
                  "Conducted over 30,000 consultations",
                  "Highest-rated astrologer on AstroLearn",
                  "Distinguished Astrology Teacher at ICAS, Bangalore",
                  "India's Top Expert in Nakshatra Nadi Astrology",
                ]}
              />
            </div>
  
            <div className="text-center mt-16">
              <CallToActionButton
                className="mx-auto"
                handleClick={() => setLeadFormOpen(true)}
                ctaText={ctaText}
              />
              <div className="text-gray-400 italic mt-4">
                *No Prior Experience Needed*
              </div>
            </div>
          </div>
        </section>
  
        {/* FAQ Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8">
              FAQ's
            </h2>
  
            <div className="text-center mb-16">
              <div className="text-xl text-white leading-relaxed">
                We've tried our best to answer all common queries that you might
                have.
              </div>
            </div>
  
            <div className="max-w-4xl mx-auto">
              <Faq />
            </div>
            <div className="text-center mb-16">
              <CallToActionButton
                className="mx-auto"
                handleClick={() => setLeadFormOpen(true)}
                ctaText={ctaText}
              />
            </div>
  
            <div className="text-gray-400 italic text-center mt-8">
              *No Prior Experience Needed*
            </div>
          </div>
        </section>
  
        {/* Bottom Sticky Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-black shadow-2xl border-t border-gray-800 p-4 z-50">
          <div className="container mx-auto flex items-center justify-between max-w-7xl max-sm:justify-center">
            <button
              className="bg-gradient-to-r from-yellow-800 via-yellow-500 to-yellow-800 !text-white text-[16px] sm:text-lg font-bold px-6 py-3 rounded-md w-full shadow-sm !border-none animate-pulse"
              onClick={() => {
                setLeadFormOpen(true);
              }}
            >
              Unlock World's First Nakshatra Nadi Ai Tool
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  function Faq() {
    return (
      <div className="w-full pb-24">
        <div className="w-full flex flex-col gap-5 items-center">
          {FAQ_Content.map((faq: any, ind) => (
            <ExpandableTile
              header={faq.question}
              key={ind}
              className="!bg-neutral-500/10 border border-white/20"
            >
              {faq.answer}
            </ExpandableTile>
          ))}
        </div>
      </div>
    );
  }
  const FAQ_Content = [
    {
      question: "Is it a live or pre-recorded workshop ?",
      answer: "It is a live workshop.",
    },
    {
      question: "Is this class for beginners?",
      answer:
        "Yes, this class is for beginners and some concepts that are easy for beginners and new to experts will also be taught.",
    },
    {
      question: "In what language this class will be conducted ?",
      answer: "The class will be in Hindi and English",
    },
    {
      question: "Are there any prerequisites?",
      answer: "Yes, All we need is your time and concentration",
    },
  ];
  